import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  Clock, 
  Eye,
  CheckCircle,
  XCircle,
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Filter,
  Send,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { JobData } from '../shared/types';
import {
  STEP_MOTION_CLASS,
  STEP_SCROLL_AREA_CLASS,
  STEP_CARD_COMPACT_CLASS,
  STEP_INNER_HEADER_CLASS,
  STEP_HEADER_ICON_CLASS,
} from '../shared/stepLayout';
import { supabase } from '@soVendor/lib/supabase';

const TRACKING_WRAPPER_CLASS =
  'w-full min-w-0 max-w-full lg:max-w-7xl xl:max-w-[90rem] lg:mx-auto px-3 xs:px-4 sm:px-5 lg:px-8 flex-1 flex flex-col min-h-0 overflow-hidden gap-0';
const TRACKING_NAV_CLASS =
  'flex-shrink-0 z-10 w-full flex justify-between items-center gap-2 sm:gap-3 pt-2.5 pb-[max(0.5rem,env(safe-area-inset-bottom))] mt-2 border-t border-gray-200 dark:border-gray-600 px-3 xs:px-4 sm:px-0 max-w-full lg:max-w-7xl xl:max-w-[90rem] lg:mx-auto';

interface ApplicationTrackingStepProps {
  onNext: () => void;
  onBack: () => void;
  jobData: JobData;
  applicationId: string | null;
  isLoading: boolean;
  error: string | null;
  onApplyForSelected?: (applicationIds: string[]) => void;
  onNewOptimization?: () => void;
}

interface JobApplication {
  id: string;
  job_title: string;
  company_name: string;
  location: string;
  application_status: 'applied' | 'viewed' | 'interview' | 'rejected' | 'not_applied' | 'saved_for_later' | 'in_progress';
  applied_date: string;
  resume_version_id?: string;
}

/* Status config helper — theme vars for dark/light */
function getStatusConfig(status: string): { label: string; color: string; bg: string; border: string } {
  switch (status) {
    case 'applied':      return { label: 'Applied',       color: 'var(--vscode-primary)',       bg: 'var(--brand-accent-bg)',   border: 'var(--brand-accent-border)' };
    case 'viewed':       return { label: 'Viewed',       color: 'var(--vscode-primary)',       bg: 'var(--brand-accent-bg)',   border: 'var(--brand-accent-border)' };
    case 'interview':    return { label: 'Interview',    color: 'var(--vscode-success)',       bg: 'var(--vscode-success-bg)', border: 'var(--vscode-success)' };
    case 'rejected':     return { label: 'Rejected',     color: 'var(--vscode-text-tertiary)',  bg: 'var(--vscode-bg-tertiary)', border: 'var(--vscode-border-primary)' };
    case 'saved_for_later': return { label: 'Saved',    color: 'var(--vscode-charts-yellow)', bg: 'var(--vscode-editor-inactiveSelectionBackground)', border: 'var(--vscode-border-primary)' };
    case 'in_progress':  return { label: 'In Progress',  color: 'var(--vscode-charts-orange)', bg: 'var(--vscode-editor-inactiveSelectionBackground)', border: 'var(--vscode-border-primary)' };
    default:             return { label: 'Not Applied',  color: 'var(--vscode-text-tertiary)', bg: 'var(--vscode-bg-tertiary)', border: 'var(--vscode-border-primary)' };
  }
}

const ALL_STATUS_FILTERS = ['all', 'applied', 'not_applied', 'in_progress', 'saved_for_later', 'interview', 'rejected'] as const;

const ApplicationTrackingStep: React.FC<ApplicationTrackingStepProps> = ({
  onNext,
  onBack,
  jobData,
  applicationId,
  onApplyForSelected,
  onNewOptimization,
}) => {
  const { t, i18n } = useTranslation();
  const isNative = Capacitor.isNativePlatform();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [recordsPerPage, setRecordsPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(1);

  const selectedApp = applications.find(a => a.id === selectedId);
  const selectedNotAppliedIds = selectedApp?.application_status === 'not_applied' ? [selectedId!] : [];
  const showApplyForJob = selectedNotAppliedIds.length > 0;

  const selectForApply = (id: string) => {
    setSelectedId(prev => (prev === id ? null : id));
  };

  const ensureJobTrackedRef = useRef(false);

  useEffect(() => {
    loadApplications();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId]);

  useEffect(() => {
    if (!jobData?.title || loading || ensureJobTrackedRef.current) return;
    const jobTitle = (jobData.title as string).trim();
    const companyName = ((jobData.company as string) || 'Not specified').trim();
    const hasMatch = applications.some(
      (a) => (a.job_title || '').trim() === jobTitle && (a.company_name || '').trim() === companyName
    );
    if (hasMatch) {
      ensureJobTrackedRef.current = true;
      return;
    }

    ensureJobTrackedRef.current = true;
    (async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) return;

        const { data: inserted, error } = await supabase
          .from('ggj_job_applications')
          .insert({
            user_id: session.user.id,
            job_title: jobTitle,
            company_name: companyName,
            job_url: (jobData as Record<string, unknown>)?.url ?? '',
            location: (jobData as Record<string, unknown>)?.location ?? '',
            application_status: 'not_applied',
            application_notes: '',
            cover_letter_snippet: '',
            application_snippet: ''
          })
          .select('id')
          .single();

        if (!error && inserted?.id) {
          loadApplications();
        }
      } catch (e) {
        console.warn('[ApplicationTrackingStep] ensureJobTracked failed:', e);
      }
    })();
  }, [loading, applications, jobData?.title, jobData?.company]);

  const loadApplications = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data, error } = await supabase
        .from('ggj_job_applications')
        .select('*')
        .eq('user_id', session.user.id)
        .order('applied_date', { ascending: false });

      if (!error && data) {
        setApplications(data as JobApplication[]);
      }
    } catch (err) {
      console.error('Failed to load applications:', err);
    } finally {
      setLoading(false);
    }
  };

  const stats = {
    applied: applications.filter(a => a.application_status === 'applied').length,
    viewed: applications.filter(a => a.application_status === 'viewed').length,
    interview: applications.filter(a => a.application_status === 'interview').length,
    rejected: applications.filter(a => a.application_status === 'rejected').length,
  };

  const filteredApps = statusFilter === 'all'
    ? applications
    : applications.filter(a => a.application_status === statusFilter);

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / recordsPerPage));
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const paginatedApps = filteredApps.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, recordsPerPage]);

  const handleRecordsPerPageChange = (value: number) => {
    setRecordsPerPage(value);
    setCurrentPage(1);
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    if (isToday) return t('optimizer.applicationTrackingStep.today');
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (d.toDateString() === yesterday.toDateString()) return t('optimizer.applicationTrackingStep.yesterday');
    return d.toLocaleDateString(i18n.language || 'en', { month: 'numeric', day: 'numeric', year: 'numeric' });
  };

  const primaryButtonClass = 'hover:opacity-90 transition-opacity font-semibold text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]';

  /* ─── NATIVE MOBILE DESIGN ──────────────────────────── */
  if (isNative) {
    const filterLabels: Record<string, string> = {
      all:            `All (${applications.length})`,
      applied:        `Applied (${stats.applied})`,
      not_applied:    `Not Applied (${applications.filter(a => a.application_status === 'not_applied').length})`,
      in_progress:    `In Progress (${applications.filter(a => a.application_status === 'in_progress').length})`,
      saved_for_later:`Saved (${applications.filter(a => a.application_status === 'saved_for_later').length})`,
      interview:      `Interview (${stats.interview})`,
      rejected:       `Rejected (${stats.rejected})`,
    };

    return (
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: 'var(--ggj-bg-page)', isolation: 'isolate' as const }}>

        {/* ── Native header (curved under notch) ── */}
        <div style={{
          background: 'var(--ggj-header-bg)',
          borderRadius: '0 0 24px 24px',
          paddingTop: 'max(calc(var(--safe-area-top, env(safe-area-inset-top, 0px)) + 8px), 40px)',
          paddingBottom: 18,
          paddingLeft: 16,
          paddingRight: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          boxShadow: 'var(--brand-cta-shadow, none)',
        }}>
          <button
            type="button"
            onClick={onBack}
            style={{ background: 'var(--ggj-header-btn-bg)', border: 'none', borderRadius: 10, width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, WebkitTapHighlightColor: 'transparent', color: 'var(--ggj-header-btn-icon)' }}
          >
            <ChevronLeft size={20} style={{ color: 'inherit' }} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontSize: 10, fontWeight: 700, color: 'var(--ggj-header-sub)', textTransform: 'uppercase', letterSpacing: '0.10em', margin: 0 }}>
              Resume Optimizer
            </p>
            <h1 style={{ fontSize: 19, fontWeight: 800, color: 'var(--ggj-header-text)', margin: '2px 0 0', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              Application Tracking
            </h1>
          </div>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: 'var(--ggj-header-btn-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: 'var(--ggj-header-btn-icon)' }}>
            <Briefcase size={18} style={{ color: 'inherit' }} />
          </div>
        </div>

        {/* ── Scrollable content ── */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' as any }}>

          {/* Stats row */}
          <div style={{ display: 'flex', gap: 8, padding: '14px 14px 0', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(108,71,255,0.10)', border: '1.5px solid rgba(108,71,255,0.25)', borderRadius: 20, padding: '6px 14px' }}>
              <span style={{ fontSize: 16, fontWeight: 800, color: '#6C47FF' }}>{applications.length}</span>
              <span style={{ fontSize: 11, fontWeight: 600, color: '#6C47FF', opacity: 0.75 }}>Total</span>
            </div>
            {stats.applied > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(37,99,235,0.10)', border: '1.5px solid rgba(37,99,235,0.25)', borderRadius: 20, padding: '6px 14px' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#2563eb' }}>{stats.applied}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#2563eb', opacity: 0.75 }}>Applied</span>
              </div>
            )}
            {stats.interview > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(5,150,105,0.10)', border: '1.5px solid rgba(5,150,105,0.25)', borderRadius: 20, padding: '6px 14px' }}>
                <span style={{ fontSize: 16, fontWeight: 800, color: '#059669' }}>{stats.interview}</span>
                <span style={{ fontSize: 11, fontWeight: 600, color: '#059669', opacity: 0.75 }}>Interviews</span>
              </div>
            )}
          </div>

          {/* Single dropdown filter */}
          <div style={{ padding: '12px 14px 4px' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: 'var(--vscode-bg-elevated)',
              border: '1.5px solid var(--brand-accent-border)',
              borderRadius: 14, padding: '0 14px',
              boxShadow: 'var(--vscode-shadow-sm)',
            }}>
              <Filter size={15} style={{ color: 'var(--vscode-primary)', flexShrink: 0 }} />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  flex: 1, minHeight: 46, background: 'transparent', border: 'none',
                  outline: 'none', fontSize: 13, fontWeight: 700,
                  color: 'var(--vscode-primary)', cursor: 'pointer',
                  WebkitAppearance: 'none', appearance: 'none',
                }}
              >
                {ALL_STATUS_FILTERS.map((s) => (
                  <option key={s} value={s}>{filterLabels[s]}</option>
                ))}
              </select>
              <ChevronRight size={15} style={{ color: 'var(--vscode-text-tertiary)', flexShrink: 0, transform: 'rotate(90deg)' }} />
            </div>
          </div>

          {/* Card list */}
          <div style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 0, paddingBottom: 24 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ggj-text-muted)' }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid var(--vscode-border-primary)', borderTop: '3px solid var(--vscode-primary)', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
                <p style={{ fontSize: 13 }}>Loading applications…</p>
              </div>
            ) : filteredApps.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <Briefcase size={40} style={{ color: 'var(--ggj-text-muted)', margin: '0 auto 12px' }} />
                <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--ggj-text-body)', marginBottom: 4 }}>
                  {t('optimizer.applicationTrackingStep.noApplications')}
                </p>
                <p style={{ fontSize: 12, color: 'var(--ggj-text-muted)' }}>
                  {t('optimizer.applicationTrackingStep.noApplicationsHint')}
                </p>
              </div>
            ) : (
              paginatedApps.map((app, index) => {
                const isCurrentApplication = applicationId && app.id === applicationId;
                const statusConf = getStatusConfig(app.application_status);
                const canSelect = app.application_status === 'not_applied';
                const isSelected = selectedId === app.id;
                const appliedLabel = app.application_status === 'saved_for_later' || !app.applied_date
                  ? 'Saved for later'
                  : formatDate(app.applied_date);

                /* Status-to-accent color for left border — theme vars */
                const accentColor =
                  app.application_status === 'applied'          ? 'var(--vscode-success)' :
                  app.application_status === 'interview'        ? 'var(--vscode-primary)' :
                  app.application_status === 'in_progress'      ? 'var(--vscode-charts-orange)' :
                  app.application_status === 'rejected'         ? 'var(--vscode-error-foreground)' :
                  app.application_status === 'saved_for_later'  ? 'var(--vscode-text-tertiary)' :
                                                                   'var(--vscode-border-primary)';

                return (
                  <div
                    key={app.id}
                    role={canSelect ? 'button' : undefined}
                    tabIndex={canSelect ? 0 : undefined}
                    onClick={canSelect ? () => selectForApply(app.id) : undefined}
                    onKeyDown={canSelect ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); selectForApply(app.id); } } : undefined}
                    style={{
                      background: 'var(--ggj-bg-card)',
                      borderRadius: 14,
                      border: `1px solid ${isSelected || isCurrentApplication ? 'var(--vscode-primary)' : 'var(--ggj-border)'}`,
                      marginBottom: 10,
                      overflow: 'hidden',
                      boxShadow: isSelected || isCurrentApplication ? 'var(--vscode-shadow-sm)' : 'var(--ggj-shadow-card)',
                      display: 'flex',
                      cursor: canSelect ? 'pointer' : 'default',
                      WebkitTapHighlightColor: canSelect ? 'transparent' : undefined,
                    }}
                  >
                    {/* Colored left status strip */}
                    <div style={{ width: 4, flexShrink: 0, background: accentColor, opacity: 0.8 }} />

                    <div style={{ flex: 1, padding: '12px 12px 12px 10px', minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
                        {/* Job info */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p
                            style={{
                              fontSize: 14, fontWeight: 700,
                              color: 'var(--ggj-text-heading)',
                              marginBottom: 2, lineHeight: 1.3,
                              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                            }}
                          >
                            {app.job_title?.trim() || 'Job application'}
                          </p>
                          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--ggj-text-body)', marginBottom: 2 }}>
                            {app.company_name?.trim() || '—'}
                          </p>
                          <p style={{ fontSize: 11, color: 'var(--ggj-text-muted)' }}>
                            {app.location?.trim() ? `${app.location.trim()} · ` : ''}{appliedLabel}
                          </p>
                        </div>

                        {/* Right: status badge + radio (larger tap target) */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                          <span
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              fontSize: 10, fontWeight: 700, letterSpacing: '0.02em',
                              color: statusConf.color, background: statusConf.bg,
                              border: `1px solid ${statusConf.border}`,
                              padding: '3px 8px', borderRadius: 20, whiteSpace: 'nowrap',
                            }}
                          >
                            {app.application_status === 'applied'    && <CheckCircle size={9} />}
                            {app.application_status === 'not_applied' && <XCircle size={9} />}
                            {['in_progress','saved_for_later','viewed'].includes(app.application_status) && <Clock size={9} />}
                            {statusConf.label}
                          </span>
                          {canSelect ? (
                            <div
                              onClick={(e) => { e.stopPropagation(); selectForApply(app.id); }}
                              style={{
                                minWidth: 44, minHeight: 44, width: 44, height: 44, borderRadius: '50%',
                                border: `2px solid ${isSelected ? 'var(--vscode-primary)' : 'var(--ggj-border)'}`,
                                background: isSelected ? 'var(--vscode-primary)' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                cursor: 'pointer', flexShrink: 0,
                                WebkitTapHighlightColor: 'transparent',
                              }}
                              aria-label={isSelected ? t('optimizer.applicationTrackingStep.deselectForApply', 'Deselect to apply for this job') : t('optimizer.applicationTrackingStep.selectForApply', 'Select to apply for this job')}
                            >
                              {isSelected && <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--vscode-primary-foreground, #fff)' }} />}
                            </div>
                          ) : isCurrentApplication ? (
                            <CheckCircle size={16} style={{ color: 'var(--vscode-primary)' }} />
                          ) : null}
                        </div>
                      </div>

                      {/* Apply for Job CTA — only for selected not-applied rows */}
                      {showApplyForJob && canSelect && isSelected && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); onApplyForSelected?.(selectedNotAppliedIds); }}
                          style={{
                            width: '100%', minHeight: 40, borderRadius: 10, marginTop: 10,
                            background: 'var(--brand-cta-bg)',
                            border: 'none', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 12, fontWeight: 700,
                            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            boxShadow: 'var(--brand-cta-shadow)',
                            WebkitTapHighlightColor: 'transparent',
                          }}
                        >
                          <Send size={13} />
                          {t('optimizer.applicationTrackingStep.applyForJob')}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Pagination — simple prev/next pill row */}
          {!loading && filteredApps.length > recordsPerPage && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '4px 14px 24px' }}>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, minHeight: 38, padding: '0 16px', borderRadius: 20,
                  border: '1.5px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)',
                  color: 'var(--vscode-text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: currentPage === 1 ? 0.4 : 1, WebkitTapHighlightColor: 'transparent',
                }}
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <span style={{ fontSize: 13, color: 'var(--vscode-text-tertiary)', fontWeight: 600 }}>
                {currentPage} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4, minHeight: 38, padding: '0 16px', borderRadius: 20,
                  border: '1.5px solid var(--vscode-border-primary)', background: 'var(--vscode-bg-elevated)',
                  color: 'var(--vscode-text-secondary)', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  opacity: currentPage === totalPages ? 0.4 : 1, WebkitTapHighlightColor: 'transparent',
                }}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>

        {/* ── Sticky bottom CTA ── */}
        <div style={{
          flexShrink: 0,
          padding: `12px 16px max(20px, env(safe-area-inset-bottom, 0px))`,
          background: 'var(--ggj-nav-bg)',
          borderTop: '1px solid var(--ggj-border)',
          backdropFilter: 'blur(12px)',
        }}>
          <button
            type="button"
            onClick={onNext}
            style={{
              width: '100%', minHeight: 52, borderRadius: 16,
              background: 'var(--brand-cta-bg)',
              border: 'none', color: 'var(--vscode-primary-foreground, #fff)', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              boxShadow: 'var(--brand-cta-shadow)',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('optimizer.nextActionModal.whatsNext')}
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    );
  }

  /* ─── WEB / DESKTOP DESIGN ──────────────────────────── */
  const StatusBadge = ({ status }: { status: string }) => {
    const statusConfigs: Record<string, { labelKey: string; icon: typeof Clock; style: React.CSSProperties }> = {
      applied: { labelKey: 'optimizer.applicationTrackingStep.applied', icon: Clock, style: { color: 'var(--vscode-charts-blue)', backgroundColor: 'var(--vscode-charts-blue)20', borderColor: 'var(--vscode-charts-blue)30' } },
      viewed: { labelKey: 'optimizer.applicationTrackingStep.viewed', icon: Eye, style: { color: 'var(--vscode-charts-purple)', backgroundColor: 'var(--vscode-charts-purple)20', borderColor: 'var(--vscode-charts-purple)30' } },
      interview: { labelKey: 'optimizer.applicationTrackingStep.interview', icon: CheckCircle, style: { color: 'var(--vscode-charts-green)', backgroundColor: 'var(--vscode-charts-green)20', borderColor: 'var(--vscode-charts-green)30' } },
      rejected: { labelKey: 'optimizer.applicationTrackingStep.rejected', icon: XCircle, style: { color: 'var(--vscode-text-tertiary)', backgroundColor: 'var(--vscode-input-background)', borderColor: 'var(--vscode-input-border)' } },
      saved_for_later: { labelKey: 'optimizer.applicationTrackingStep.savedForLater', icon: Clock, style: { color: 'var(--vscode-charts-yellow)', backgroundColor: 'var(--vscode-charts-yellow)20', borderColor: 'var(--vscode-charts-yellow)30' } },
      not_applied: { labelKey: 'optimizer.applicationTrackingStep.notApplied', icon: XCircle, style: { color: 'var(--vscode-text-tertiary)', backgroundColor: 'var(--vscode-input-background)', borderColor: 'var(--vscode-input-border)' } },
      in_progress: { labelKey: 'optimizer.applicationTrackingStep.inProgress', icon: Clock, style: { color: 'var(--vscode-charts-orange)', backgroundColor: 'var(--vscode-charts-orange)20', borderColor: 'var(--vscode-charts-orange)30' } }
    };

    const config = statusConfigs[status] || statusConfigs.applied;
    const Icon = config.icon;

    return (
      <span 
        className="inline-flex items-center px-2 py-1 rounded text-sm font-medium border shrink-0"
        style={config.style}
      >
        <Icon className="w-3 h-3 mr-1" />
        {t(config.labelKey)}
      </span>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.3 }}
      className={STEP_MOTION_CLASS}
    >
      <div className={TRACKING_WRAPPER_CLASS}>
        <div className={STEP_SCROLL_AREA_CLASS}>
          <div className={`${STEP_CARD_COMPACT_CLASS} shadow-2xl border-2 border-gray-200 dark:border-gray-600`}>
          <div className={`${STEP_INNER_HEADER_CLASS} flex items-start gap-3`}>
            <div className={`${STEP_HEADER_ICON_CLASS} h-10 w-10`}>
              <Briefcase className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('optimizer.applicationTrackingStep.title')}</h2>
              <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">{t('optimizer.applicationTrackingStep.subtitle')}</p>
            </div>
          </div>

          <div className="p-4 sm:p-5 min-w-0 flex-1 flex flex-col min-h-0">
          <div className="vscode-bg-secondary rounded-lg p-3 border vscode-border flex flex-col sm:flex-row gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 vscode-text-tertiary" />
              <label htmlFor="status-filter" className="text-base vscode-text-secondary whitespace-nowrap">{t('optimizer.applicationTrackingStep.status')}</label>
              <select
                id="status-filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="vscode-input rounded-lg px-3 py-2 vscode-text-primary text-base focus:outline-none focus:ring-2 focus:ring-[var(--vscode-primary)] min-w-[140px] min-h-[44px] touch-manipulation"
              >
                <option value="all">{t('optimizer.applicationTrackingStep.all', { count: applications.length })}</option>
                <option value="applied">{t('optimizer.applicationTrackingStep.applied')} ({stats.applied})</option>
                <option value="in_progress">{t('optimizer.applicationTrackingStep.inProgress')} ({applications.filter(a => a.application_status === 'in_progress').length})</option>
                <option value="not_applied">{t('optimizer.applicationTrackingStep.notApplied')} ({applications.filter(a => a.application_status === 'not_applied').length})</option>
                <option value="saved_for_later">{t('optimizer.applicationTrackingStep.savedForLater')} ({applications.filter(a => a.application_status === 'saved_for_later').length})</option>
                <option value="viewed">{t('optimizer.applicationTrackingStep.viewed')} ({stats.viewed})</option>
                <option value="interview">{t('optimizer.applicationTrackingStep.interview')} ({stats.interview})</option>
                <option value="rejected">{t('optimizer.applicationTrackingStep.rejected')} ({stats.rejected})</option>
              </select>
            </div>
          </div>

          <div className="vscode-bg-secondary rounded-lg border vscode-border flex flex-col shadow-sm overflow-hidden" style={{ minWidth: 0 }}>
            {loading ? (
              <div className="text-center py-10 text-sm vscode-text-tertiary">{t('optimizer.applicationTrackingStep.loading')}</div>
            ) : filteredApps.length === 0 ? (
              <div className="text-center py-10 vscode-text-tertiary">
                <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm font-medium vscode-text-secondary">{t('optimizer.applicationTrackingStep.noApplications')}</p>
                <p className="text-xs mt-1">{t('optimizer.applicationTrackingStep.noApplicationsHint')}</p>
              </div>
            ) : (
              <>
                <div className="lg:hidden flex flex-col divide-y vscode-border">
                  {paginatedApps.map((app, index) => {
                    const isCurrentApplication = applicationId && app.id === applicationId;
                    const hasCompany = !!app.company_name?.trim();
                    const hasLocation = !!app.location?.trim();
                    const jobTitle = app.job_title?.trim() || 'Job application';
                    const appliedLabel = app.application_status === 'saved_for_later' || !app.applied_date
                      ? t('optimizer.applicationTrackingStep.savedForLaterLabel')
                      : formatDate(app.applied_date);
                    const serialNumber = startIndex + index + 1;
                    const isApplied = app.application_status === 'applied';
                    const isInProgress = app.application_status === 'in_progress';
                    const canSelect = app.application_status === 'not_applied';
                    const isSelected = selectedId === app.id;
                    const cardBg: React.CSSProperties = isApplied
                      ? { backgroundColor: 'color-mix(in srgb, var(--vscode-charts-blue) 14%, transparent)' }
                      : isInProgress
                        ? { backgroundColor: 'color-mix(in srgb, var(--vscode-charts-orange) 14%, transparent)' }
                        : {};
                    if (isCurrentApplication) cardBg.boxShadow = 'inset 0 0 0 2px var(--vscode-primary)';
                    return (
                      <div
                        key={app.id}
                        className="p-4 vscode-text-primary"
                        style={Object.keys(cardBg).length ? cardBg : undefined}
                      >
                        <div className="flex gap-3 min-w-0">
                          {canSelect ? (
                            <label className="flex-shrink-0 flex items-center cursor-pointer touch-manipulation min-h-[44px]">
                              <input
                                type="radio"
                                name="apply-for-job-mobile"
                                checked={isSelected}
                                onChange={() => selectForApply(app.id)}
                                className="w-5 h-5 border vscode-border vscode-bg-secondary text-[var(--vscode-primary)] focus:ring-2 focus:ring-[var(--vscode-primary)]"
                                aria-label={`Select ${jobTitle}`}
                              />
                            </label>
                          ) : (
                            <span className="flex-shrink-0 w-5 h-5 flex items-center justify-center" aria-hidden>
                              {isCurrentApplication && <CheckCircle className="w-4 h-4" style={{ color: 'var(--vscode-primary)' }} />}
                            </span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 gap-y-0.5">
                              <span className="text-sm vscode-text-tertiary">{serialNumber}</span>
                              <StatusBadge status={app.application_status} />
                            </div>
                            <p className={`font-medium mt-0.5 line-clamp-2 text-base ${!app.job_title?.trim() ? 'italic vscode-text-tertiary' : ''}`} title={app.job_title || undefined}>
                              {jobTitle}
                            </p>
                            <p className={`text-sm mt-0.5 ${!hasCompany ? 'vscode-text-tertiary italic' : 'vscode-text-secondary'}`}>
                              {hasCompany ? app.company_name!.trim() : '—'}
                            </p>
                            <p className="text-sm vscode-text-tertiary mt-0.5">
                              {hasLocation ? app.location!.trim() : null}
                              {hasLocation && appliedLabel ? ' · ' : null}
                              {appliedLabel}
                            </p>
                            {showApplyForJob && canSelect && isSelected && (
                              <button
                                type="button"
                                onClick={() => onApplyForSelected?.(selectedNotAppliedIds)}
                                className="mt-2 min-h-[44px] px-4 py-2 rounded-xl text-sm font-medium transition-opacity hover:opacity-90 touch-manipulation w-full sm:w-auto text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
                                style={{ background: 'var(--brand-cta-bg)' }}
                              >
                                {t('optimizer.applicationTrackingStep.applyForJob')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="hidden lg:block min-w-[720px] overflow-x-auto">
                  <table className="ggj-data-table table-fixed">
                    <thead className="vscode-bg-primary/50 border-b vscode-border">
                      <tr>
                        <th className="w-10 py-2.5 text-center text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.select')}</th>
                        <th className="w-8 py-2.5 pl-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.no')}</th>
                        <th className="w-[22%] py-2.5 pl-2 pr-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.jobTitle')}</th>
                        <th className="w-[18%] py-2.5 pl-2 pr-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.company')}</th>
                        <th className="w-[12%] py-2.5 pl-2 pr-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.location')}</th>
                        <th className="w-[14%] py-2.5 pl-2 pr-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.date')}</th>
                        <th className="w-[14%] py-2.5 pl-2 pr-2 text-left text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.statusCol')}</th>
                        <th className="w-[12%] py-2.5 pr-2 text-center text-xs font-extrabold vscode-text-primary uppercase tracking-wide">{t('optimizer.applicationTrackingStep.action')}</th>
                      </tr>
                    </thead>
                    <tbody className="vscode-text-primary">
                      {paginatedApps.map((app, index) => {
                        const isCurrentApplication = applicationId && app.id === applicationId;
                        const hasCompany = !!app.company_name?.trim();
                        const hasLocation = !!app.location?.trim();
                        const jobTitle = app.job_title?.trim() || 'Job application';
                        const appliedLabel = app.application_status === 'saved_for_later' || !app.applied_date
                          ? t('optimizer.applicationTrackingStep.savedForLaterLabel')
                          : formatDate(app.applied_date);
                        const serialNumber = startIndex + index + 1;
                        const isApplied = app.application_status === 'applied';
                        const isInProgress = app.application_status === 'in_progress';
                        const rowStyle: React.CSSProperties = isApplied
                          ? { backgroundColor: 'color-mix(in srgb, var(--vscode-charts-blue) 14%, transparent)' }
                          : isInProgress
                            ? { backgroundColor: 'color-mix(in srgb, var(--vscode-charts-orange) 14%, transparent)' }
                            : {};
                        if (isCurrentApplication) rowStyle.boxShadow = 'inset 0 0 0 1px var(--vscode-primary)';
                        return (
                          <tr
                            key={app.id}
                            className={`border-b vscode-border/50 ${isCurrentApplication ? 'vscode-bg-primary/5' : ''} hover:vscode-bg-hover/50`}
                            style={Object.keys(rowStyle).length ? rowStyle : undefined}
                          >
                            <td className="py-2.5 text-center align-middle">
                              {app.application_status === 'not_applied' ? (
                                <label className="inline-flex items-center cursor-pointer">
                                  <input
                                    type="radio"
                                    name="apply-for-job"
                                    checked={selectedId === app.id}
                                    onChange={() => selectForApply(app.id)}
                                    className="border vscode-border vscode-bg-secondary text-[var(--vscode-primary)] focus:ring-[var(--vscode-primary)]"
                                    aria-label={`Select ${jobTitle}`}
                                  />
                                </label>
                              ) : (
                                <span className="inline-block w-4" aria-hidden />
                              )}
                            </td>
                            <td className="py-2.5 pl-2 vscode-text-tertiary text-xs align-middle">{serialNumber}</td>
                            <td className="py-2.5 pl-2 pr-2 align-middle min-w-0">
                              <div className="flex items-center gap-1.5 min-w-0">
                                {isCurrentApplication && (
                                  <CheckCircle className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--vscode-primary)' }} title="Just applied" />
                                )}
                                <span className={`font-medium truncate block min-w-0 ${!app.job_title?.trim() ? 'italic vscode-text-tertiary' : ''}`} title={app.job_title || undefined}>
                                  {jobTitle}
                                </span>
                              </div>
                            </td>
                            <td className="py-2.5 pl-2 pr-2 align-middle min-w-0">
                              <span className={`truncate block min-w-0 ${!hasCompany ? 'vscode-text-tertiary italic text-xs' : ''}`} title={hasCompany ? app.company_name! : undefined}>
                                {hasCompany ? app.company_name!.trim() : '—'}
                              </span>
                            </td>
                            <td className="py-2.5 pl-2 pr-2 align-middle min-w-0">
                              <span className={`truncate block min-w-0 text-xs ${!hasLocation ? 'vscode-text-tertiary italic' : 'vscode-text-tertiary'}`} title={hasLocation ? app.location! : undefined}>
                                {hasLocation ? app.location!.trim() : '—'}
                              </span>
                            </td>
                            <td className="py-2.5 pl-2 pr-2 vscode-text-tertiary text-xs whitespace-nowrap align-middle">{appliedLabel}</td>
                            <td className="py-2.5 pl-2 pr-2 align-middle">
                              <StatusBadge status={app.application_status} />
                            </td>
                            <td className="py-2.5 pr-2 text-center align-middle">
                              {showApplyForJob && app.application_status === 'not_applied' && selectedId === app.id && (
                                <button
                                  type="button"
                                  onClick={() => onApplyForSelected?.(selectedNotAppliedIds)}
                                  className="px-2 py-1 rounded-lg text-xs font-medium transition-opacity hover:opacity-90 text-white dark:border dark:border-[rgba(242,242,243,0.25)] dark:text-[#F2F2F3]"
                                  style={{ background: 'var(--brand-cta-bg)' }}
                                >
                                  Apply
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {!loading && filteredApps.length > 0 && (
              <div className="border-t vscode-border px-3 sm:px-4 py-3 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] sm:pb-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-between gap-3 vscode-bg-primary/30 min-w-0">
                <span className="text-base vscode-text-secondary order-1 sm:order-none">
                  {t('optimizer.applicationTrackingStep.showing', { start: startIndex + 1, end: Math.min(endIndex, filteredApps.length), total: filteredApps.length })}
                </span>
                <div className="flex flex-wrap items-center gap-3 sm:gap-4 order-2 sm:order-none">
                  <label className="flex items-center gap-2 text-base vscode-text-secondary">
                    <span className="hidden sm:inline">Show items per page:</span>
                    <select
                      value={recordsPerPage}
                      onChange={(e) => handleRecordsPerPageChange(Number(e.target.value))}
                      className="py-2 px-3 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary text-base min-w-[4rem] min-h-[44px] touch-manipulation"
                      aria-label="Items per page"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="py-2 px-3 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--vscode-list-hoverBackground)] min-h-[44px] touch-manipulation"
                      aria-label="Previous page"
                    >
                      {t('optimizer.applicationTrackingStep.prev')}
                    </button>
                    <span className="text-base vscode-text-tertiary px-1 min-w-[4rem] text-center">
                      {t('optimizer.applicationTrackingStep.pageOf', { current: currentPage, total: totalPages })}
                    </span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="py-2 px-3 rounded-lg border vscode-border vscode-bg-primary vscode-text-primary text-base disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[var(--vscode-list-hoverBackground)] min-h-[44px] touch-manipulation"
                      aria-label="Next page"
                    >
                      {t('optimizer.applicationTrackingStep.next')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
          </div>
        </div>
        </div>

        <nav className={TRACKING_NAV_CLASS} aria-label="Step navigation">
          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBack(); }}
            className="flex items-center justify-center gap-2 min-h-[44px] px-3 sm:px-4 py-3 vscode-text-secondary hover:vscode-text-primary font-medium rounded-lg transition-colors text-sm cursor-pointer border vscode-border vscode-bg-secondary hover:opacity-90 touch-manipulation flex-initial min-w-0"
            aria-label="Back"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" />
            <span className="hidden sm:inline">{t('optimizer.applyStep.back')}</span>
          </button>

          <button
            type="button"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onNext(); }}
            className={`flex items-center justify-center gap-2 min-h-[44px] px-4 sm:px-5 py-3 rounded-lg text-sm cursor-pointer touch-manipulation flex-initial min-w-0 ${primaryButtonClass}`}
            style={{ background: 'var(--brand-cta-bg)' }}
            aria-label={t('optimizer.nextActionModal.whatsNext')}
          >
            <span className="hidden sm:inline">{t('optimizer.nextActionModal.whatsNext')}</span>
            <ArrowRight className="w-4 h-4 shrink-0" />
          </button>
        </nav>
      </div>
    </motion.div>
  );
};

export default React.memo(ApplicationTrackingStep);
